/**
 * App-wide light/dark mode. Three settings — 'light', 'dark', 'system' —
 * persisted to localStorage. 'system' follows the OS via prefers-color-scheme
 * and updates live. The resolved appearance ('light' | 'dark') is fed to the
 * root Radix <Theme appearance>, so every Radix-token surface flips for free.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Appearance = 'light' | 'dark';

const STORAGE_KEY = 'themeMode';

interface ThemeModeValue {
  mode: ThemeMode;
  appearance: Appearance; // resolved (system → the OS value)
  setMode: (m: ThemeMode) => void;
  toggle: () => void; // convenience: flip resolved light↔dark
}

const Ctx = createContext<ThemeModeValue>({
  mode: 'system',
  appearance: 'light',
  setMode: () => {},
  toggle: () => {},
});

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* storage disabled */ }
  return 'system';
}

function systemAppearance(): Appearance {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStored);
  const [sysAppearance, setSysAppearance] = useState<Appearance>(systemAppearance);

  // Track the OS preference while in 'system' mode (and always, cheaply).
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const onChange = () => setSysAppearance(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const appearance: Appearance = mode === 'system' ? sysAppearance : mode;

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => {
    setMode(appearance === 'dark' ? 'light' : 'dark');
  }, [appearance, setMode]);

  // Reflect the resolved appearance on <html> so non-Radix / bespoke CSS can
  // key off it too (e.g. `:root.dark`), and set color-scheme for native
  // form controls / scrollbars.
  useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle('dark', appearance === 'dark');
    el.classList.toggle('light', appearance === 'light');
    el.style.colorScheme = appearance;
  }, [appearance]);

  const value = useMemo(
    () => ({ mode, appearance, setMode, toggle }),
    [mode, appearance, setMode, toggle],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useThemeMode() {
  return useContext(Ctx);
}
